import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  InputAdornment,
  TextField,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import FilterSelectInput from 'src/common/FilterSelectInput';
import SiteManagerListTable from 'src/components/siteManager/SiteManagerListTable';
import NewSiteManager from 'src/components/siteManager/NewSiteManager';
import useAuth from 'src/context/AuthContext';
import { isAdminUser } from 'src/utils/getInitials';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';

const SiteManagers = ({ location }) => {
  const {
    search, site, setSearch, setSite
  } = usePersistentListFilters();
  const theme = useTheme();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(site);
  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState(search);

  const handleSearch = (e) => { setSearchText(e.target.value); setSearch(e.target.value); };

  return (
    <>
      <Helmet>
        <title>Site Supervisors | Retrack FMS</title>
      </Helmet>

      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* Header Section */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            {/* Left */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                SITE SUPERVISORS
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Manage site-level supervisor accounts and access.
              </Typography>
            </Box>

            {/* Right */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <TextField
                placeholder="Search supervisors..."
                size="small"
                value={searchText}
                onChange={handleSearch}
                sx={{
                  width: { xs: '100%', md: 320 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={(value) => { setSiteFilter(value); setSite(value); }}
                  sx={{ width: { xs: '100%', md: 220 }, mr: 0 }}
                />
              )}

              {isAdminUser(getUser) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  Add Supervisor
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Table */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <SiteManagerListTable
              refreshData={location.state?.fromDashboard}
              searchText={searchText}
              siteFilter={siteFilter}
            />
          </Box>

          <NewSiteManager
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />
        </Container>
      </Box>
    </>
  );
};

SiteManagers.propTypes = {
  location: PropTypes.object
};

export default SiteManagers;
